import * as React from 'react';
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import List from 'antd/lib/list';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import Text from 'antd/lib/typography/Text';
import * as API from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { AppComponentProps } from '../../typescriptHelpers';
import { AppSettingsUpdatable } from '../../actions/updatable';
import { RootState } from '../../reducers/reducers';
import { selectAuthCredentials } from '../../selectors/selectors';
import { tryUpdateAppSettings } from '../../actions/appSettings';
import { globalMessage } from '../../utils/globalMessage';
import { TranslationDirectory } from '../../types';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface TranslationDirsModalProps extends AppComponentProps {
    open: boolean;
    initialDirs: TranslationDirectory[];
    onCancel: () => void;
}

const TranslationDirectoriesModal: React.FC<TranslationDirsModalProps> = (props: TranslationDirsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [directoryNames, setDirectoryNames] = React.useState(
        props.initialDirs ? props.initialDirs.map((dir) => dir.name) : [],
    );

    const editTranslationDirectoriesIsLoading = useIsLoading(AppSettingsUpdatable.UpdateTranslationDirectories);
    const editTranslationDirectoriesError = useError(AppSettingsUpdatable.UpdateTranslationDirectories);
    const editTranslationDirectoriesSuccess = useUpdated(AppSettingsUpdatable.UpdateTranslationDirectories);

    const [isGetTranslationDirsLoading, setIsGetTranslationDirsLoading] = React.useState(false);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const getDefaultTranslationDirs = async () => {
        setIsGetTranslationDirsLoading(true);
        try {
            const fetchedDefaultDirs = await API.getDefaultTranslationDirectories(
                { orgId: props.orgId, appId: props.appId },
                authCredentials,
            );
            setDirectoryNames(fetchedDefaultDirs.map((dir) => dir.name));
        } catch (error) {
            globalMessage.error(`Error getting default directories: ${error.message}`);
        }
        setIsGetTranslationDirsLoading(false);
    };

    const saveTranslationDirs = (newDirNames) => {
        dispatch(
            tryUpdateAppSettings(AppSettingsUpdatable.UpdateTranslationDirectories, {
                orgId: props.orgId,
                appId: props.appId,
                directories: newDirNames,
            }),
        );
    };

    React.useEffect(() => {
        if (editTranslationDirectoriesError) {
            globalMessage.error(editTranslationDirectoriesError);
        }
    }, [editTranslationDirectoriesError]);

    React.useEffect(() => {
        if (editTranslationDirectoriesSuccess) {
            globalMessage.success('Translation directories updated!');

            props.onCancel();
        }
    }, [editTranslationDirectoriesSuccess]);

    return (
        <Modal
            title="Edit localization directories"
            footer={
                <div style={{ display: 'flex' }}>
                    <div style={{ flex: 1 }} />
                    <Button
                        type="link"
                        loading={isGetTranslationDirsLoading}
                        onClick={getDefaultTranslationDirs}
                        disabled={editTranslationDirectoriesIsLoading}
                    >
                        Find recommended directories
                    </Button>
                    <Button onClick={props.onCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={editTranslationDirectoriesIsLoading}
                        onClick={() => saveTranslationDirs(directoryNames)}
                        disabled={isGetTranslationDirsLoading}
                    >
                        Save
                    </Button>
                </div>
            }
            open={props.open}
            onCancel={props.onCancel}
            afterClose={() => {
                setDirectoryNames(props.initialDirs.map((dir) => dir.name));
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text>Specify the directory path(s) that contain localization files.</Text>

                <Form
                    form={form}
                    style={{
                        display: 'flex',
                        marginTop: 24,
                    }}
                    onFinish={(values) => {
                        const newDirName = values.path;
                        if (newDirName) {
                            setDirectoryNames((prev) => [...prev, newDirName]);
                            form.resetFields();
                        }
                    }}
                >
                    <Form.Item
                        style={{ marginRight: 12, flex: 1 }}
                        name="path"
                        rules={[{ required: true, message: 'Please enter a directory path.' }]}
                    >
                        <Input placeholder={`path/to/localizations/directory`} autoFocus allowClear />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
                        Add
                    </Button>
                </Form>

                <List
                    style={{ backgroundColor: 'white' }}
                    bordered
                    size="small"
                    locale={{ emptyText: 'No directories added yet' }}
                    dataSource={directoryNames}
                    renderItem={(directoryName, index) => (
                        <List.Item key={index}>
                            <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        flex: 1,
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {directoryName}
                                </div>
                                <Button
                                    danger
                                    size="small"
                                    key={index}
                                    icon={<DeleteOutlined />}
                                    type="link"
                                    onClick={() =>
                                        setDirectoryNames((prev) => {
                                            const newDirNames = [...prev];
                                            newDirNames.splice(index, 1);
                                            return newDirNames;
                                        })
                                    }
                                />
                            </div>
                        </List.Item>
                    )}
                />
            </div>
        </Modal>
    );
};

export default TranslationDirectoriesModal;
